// Setup catalog form polish — shared guided inputs for the Add/Edit catalog
// modals: Owner-role multiselect chips, Tier radio group (Critical /
// Important / Low display vocabulary), and the Due-date-formula picker that
// composes the legacy string grammar (raw text stays reachable via the
// Advanced toggle so an unparseable existing value is never stranded).

import { useState } from "react";
import { Button, FormField, Input } from "@/design-system/components/ui";
import { formatCalendarDate } from "@/design-system/lib/format";
import {
  LOAN_SETUP_TIER_LABEL,
  SETUP_OWNER_ROLE_CODES,
  SETUP_OWNER_ROLE_LABEL,
  type LoanSetupTier,
} from "@/api/portfolioAdmin/types";
import {
  DUE_DATE_ANCHORS,
  DUE_DATE_UNIT_LABEL,
  composeDueDateFormula,
  parseDueDateFormula,
  previewDueDate,
  type DueDateAnchor,
  type DueDateFormulaModel,
  type DueDateOffsetUnit,
} from "./setupCatalogForm";

const SELECT_CLASS =
  "px-space-2 py-space-1 border border-border-light rounded-md text-small bg-white-pure";

// ---------------------------- owner roles -----------------------------

interface OwnerRolesFieldProps {
  /** Selected role codes in storage order. */
  value: string[];
  onChange: (codes: string[]) => void;
}

export function OwnerRolesField({ value, onChange }: OwnerRolesFieldProps) {
  // Legacy codes on existing rows (e.g. "E-VM", "A3") stay selectable so
  // open-edit-save-unchanged round-trips byte-identically.
  const legacy = value.filter(
    (c) => !(SETUP_OWNER_ROLE_CODES as readonly string[]).includes(c),
  );
  const options = [...SETUP_OWNER_ROLE_CODES, ...legacy];

  const toggle = (code: string) => {
    onChange(
      value.includes(code) ? value.filter((c) => c !== code) : [...value, code],
    );
  };

  return (
    <FormField
      label="Owner roles"
      required
      helperText="Select every role that owns this item."
    >
      <div
        className="flex flex-wrap gap-space-2"
        role="group"
        aria-label="Owner roles"
        data-testid="setup-catalog-owner-roles"
      >
        {options.map((code) => {
          const active = value.includes(code);
          const label = SETUP_OWNER_ROLE_LABEL[code];
          return (
            <button
              key={code}
              type="button"
              aria-pressed={active}
              onClick={() => toggle(code)}
              data-testid={`setup-owner-role-${code}`}
              className={`inline-flex items-baseline gap-space-1 rounded-pill border px-space-3 py-[5px] text-caption font-medium transition-colors focus-visible:outline-none focus-visible:shadow-goldRing ${
                active
                  ? "border-gold bg-gold text-onyx"
                  : "border-border-light bg-white-pure text-gray-mid hover:border-gold hover:text-onyx"
              }`}
            >
              <span className="font-bold">{code}</span>
              {label && <span>{label}</span>}
            </button>
          );
        })}
      </div>
    </FormField>
  );
}

// ------------------------------- tier ----------------------------------

const TIER_DESCRIPTION: Record<LoanSetupTier, string> = {
  DealKiller: "Blocks setup completion",
  RedFlag: "Blocks setup completion",
  NeedsImprovement: "Does not block",
};

const TIER_TEXT_CLASS: Record<LoanSetupTier, string> = {
  DealKiller: "text-status-error",
  RedFlag: "text-status-warning",
  NeedsImprovement: "text-status-info",
};

interface TierFieldProps {
  value: LoanSetupTier;
  onChange: (tier: LoanSetupTier) => void;
}

export function TierField({ value, onChange }: TierFieldProps) {
  return (
    <FormField label="Tier" required>
      <div
        role="radiogroup"
        aria-label="Tier"
        className="flex flex-col gap-space-1"
        data-testid="setup-catalog-tier"
      >
        {(Object.keys(LOAN_SETUP_TIER_LABEL) as LoanSetupTier[]).map((tier) => (
          <label
            key={tier}
            className={`flex items-center gap-space-2 rounded-md border px-space-2 py-space-1 cursor-pointer ${
              value === tier ? "border-gold bg-white-warm" : "border-border-light"
            }`}
          >
            <input
              type="radio"
              name="setup-catalog-tier"
              value={tier}
              checked={value === tier}
              onChange={() => onChange(tier)}
              data-testid={`setup-tier-${tier}`}
            />
            <span
              className={`text-caption font-display font-bold uppercase tracking-label ${TIER_TEXT_CLASS[tier]}`}
            >
              {LOAN_SETUP_TIER_LABEL[tier]}
            </span>
            <span className="text-caption text-gray-mid">
              {TIER_DESCRIPTION[tier]}
            </span>
          </label>
        ))}
      </div>
    </FormField>
  );
}

// -------------------------- due date formula ---------------------------

interface DueDateFormulaFieldProps {
  /** The stored formula string — single source of truth; the picker only
   *  recomposes it on interaction, so an untouched value round-trips
   *  byte-identically (FIND-12). */
  value: string;
  onChange: (formula: string) => void;
  /** Loan context for the "On this loan: …" preview (first-payment
   *  anchors only — the close date isn't part of the loan summary). */
  firstPaymentDate?: string | null;
}

export function DueDateFormulaField({
  value,
  onChange,
  firstPaymentDate,
}: DueDateFormulaFieldProps) {
  const model = parseDueDateFormula(value);
  // An unparseable value forces Advanced so it's never stranded or mangled.
  const [advancedChosen, setAdvancedChosen] = useState(false);
  const advanced = advancedChosen || model === null;

  const apply = (next: DueDateFormulaModel) => onChange(composeDueDateFormula(next));

  const onAnchorChange = (anchor: DueDateAnchor) => {
    const option = DUE_DATE_ANCHORS.find((a) => a.value === anchor)!;
    const unit = model && option.units.includes(model.unit) ? model.unit : option.units[0];
    const offset = option.offsetRequired ? (model?.offset ?? 60) : (model?.offset ?? null);
    apply({ anchor, offset, unit });
  };

  const anchorOption = model
    ? DUE_DATE_ANCHORS.find((a) => a.value === model.anchor)!
    : null;
  const preview = model ? previewDueDate(model, firstPaymentDate) : null;

  return (
    <FormField
      label="Due date"
      required
      helperText="Drives the due date when a checklist is generated."
    >
      <div className="flex flex-col gap-space-2" data-testid="setup-catalog-due-date">
        {!advanced && model && anchorOption && (
          <>
            <div className="flex flex-wrap items-center gap-space-2">
              <select
                className={SELECT_CLASS}
                value={model.anchor}
                onChange={(e) => onAnchorChange(e.target.value as DueDateAnchor)}
                aria-label="Anchor"
                data-testid="setup-due-anchor"
              >
                {DUE_DATE_ANCHORS.map((a) => (
                  <option key={a.value} value={a.value}>
                    {a.label}
                  </option>
                ))}
              </select>
              <span className="text-small text-gray-mid">+</span>
              <Input
                type="number"
                min={0}
                className="w-24"
                value={model.offset ?? ""}
                onChange={(e) => {
                  const raw = e.target.value;
                  const offset =
                    raw === ""
                      ? anchorOption.offsetRequired
                        ? 0
                        : null
                      : Math.max(0, Number(raw));
                  apply({ ...model, offset });
                }}
                aria-label="Offset"
                placeholder="No offset"
                data-testid="setup-due-offset"
              />
              <select
                className={SELECT_CLASS}
                value={model.unit}
                onChange={(e) =>
                  apply({ ...model, unit: e.target.value as DueDateOffsetUnit })
                }
                disabled={model.offset === null}
                aria-label="Offset unit"
                data-testid="setup-due-unit"
              >
                {anchorOption.units.map((u) => (
                  <option key={u} value={u}>
                    {DUE_DATE_UNIT_LABEL[u]}
                  </option>
                ))}
              </select>
            </div>
            <p className="text-caption text-gray-mid m-0" data-testid="setup-due-composed">
              Stored as: <span className="font-medium text-onyx">{value}</span>
            </p>
            {preview && (
              <p className="text-caption text-gray-mid m-0" data-testid="setup-due-preview">
                On this loan: {formatCalendarDate(preview)}
              </p>
            )}
            {anchorOption.deferred && (
              <p className="text-caption text-gray-mid m-0">
                Due date is set once the {anchorOption.label.toLowerCase()} is
                recorded on the loan.
              </p>
            )}
          </>
        )}

        {advanced && (
          <>
            <Input
              value={value}
              onChange={(e) => onChange(e.target.value)}
              aria-label="Due date formula"
              data-testid="setup-due-advanced-input"
            />
            <p className="text-caption text-gray-mid m-0">
              {model === null
                ? "This formula isn't expressible in the picker — edit the raw text."
                : 'e.g. "Close + 30 days", "FirstPaymentDate", "UCC filing + 30 days"'}
            </p>
          </>
        )}

        <Button
          variant="text"
          size="sm"
          className="self-start"
          onClick={() => setAdvancedChosen(!advanced)}
          disabled={advanced && model === null}
          data-testid="setup-due-advanced-toggle"
        >
          {advanced ? "Use picker" : "Advanced"}
        </Button>
      </div>
    </FormField>
  );
}
